import { useState } from "react";
import { Link } from "react-router-dom";
import type { Guide } from "../guideData";

const difficultyConfig = {
  Beginner: { color: "text-emerald-700 bg-emerald-50 border-emerald-200", dot: "bg-emerald-500" },
  Intermediate: { color: "text-amber-700 bg-amber-50 border-amber-200", dot: "bg-amber-500" },
  Advanced: { color: "text-red-700 bg-red-50 border-red-200", dot: "bg-red-500" },
};

type Props = { guide: Guide };

export default function GuideCard({ guide }: Props) {
  const [expanded, setExpanded] = useState(false);
  const diff = difficultyConfig[guide.difficulty];

  return (
    <div
      className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
        expanded ? "border-amber-300 bg-white" : "border-gray-200 bg-white hover:border-amber-200"
      }`}
    >
      {/* Card header */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl shrink-0">
            <i className={`${guide.icon} text-xl text-amber-500`}></i>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${diff.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${diff.dot}`}></span>
              {guide.difficulty}
            </span>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-time-line text-xs"></i>
              </span>
              {guide.time}
            </span>
          </div>
        </div>

        <h3 className="font-bold text-gray-900 text-sm leading-snug mb-2">{guide.title}</h3>
        <p className="text-gray-500 text-xs leading-relaxed">{guide.summary}</p>

        <button
          type="button"
          onClick={() => setExpanded((p) => !p)}
          className={`mt-4 w-full flex items-center justify-center gap-2 text-xs font-semibold py-2.5 rounded-xl transition-all cursor-pointer ${
            expanded
              ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
              : "bg-amber-500 text-white hover:bg-amber-400"
          }`}
        >
          <span className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={`${expanded ? "ri-eye-off-line" : "ri-eye-line"} text-sm`}></i>
          </span>
          {expanded ? "Hide Guide" : "View Step-by-Step Guide"}
        </button>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-100 px-5 pb-6 pt-5 space-y-5">

          {/* Tools needed */}
          <div>
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">What You&apos;ll Need</p>
            <ul className="space-y-1.5">
              {guide.tools.map((tool, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                  <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-amber-500 text-sm"></i>
                  </div>
                  {tool}
                </li>
              ))}
            </ul>
          </div>

          {/* Steps */}
          <div>
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">Steps</p>
            <ol className="space-y-3">
              {guide.steps.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-gray-900 text-white text-xs font-bold rounded-full shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 leading-snug">{step.title}</p>
                    <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{step.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Tip */}
          {guide.tip && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-lightbulb-line text-base text-amber-500"></i>
              </div>
              <p className="text-xs text-amber-800 leading-relaxed"><strong>Pro tip:</strong> {guide.tip}</p>
            </div>
          )}

          {/* Warning */}
          {guide.warning && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-alert-line text-base text-red-500"></i>
              </div>
              <p className="text-xs text-red-800 leading-relaxed"><strong>Safety warning:</strong> {guide.warning}</p>
            </div>
          )}

          {/* Shop Recommendation Block */}
          {guide.shopRec && (
            <div className="border border-amber-200 bg-amber-50 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-amber-200 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-shopping-bag-line text-amber-600 text-sm"></i>
                </div>
                <p className="text-xs font-bold text-amber-900 uppercase tracking-wider">{guide.shopRec.label}</p>
              </div>
              <div className="px-4 py-3">
                <p className="text-xs text-amber-800 leading-relaxed mb-3">{guide.shopRec.desc}</p>
                <div className="grid grid-cols-1 gap-1.5 mb-3">
                  {guide.shopRec.tiers.map((tier) => (
                    <Link
                      key={tier.name}
                      to={tier.link}
                      className="flex items-center justify-between px-3 py-2 bg-white border border-amber-200 rounded-lg hover:border-amber-400 hover:bg-amber-50 transition-all cursor-pointer group"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                          <i className="ri-arrow-right-s-line text-amber-500 text-sm"></i>
                        </div>
                        <span className="text-xs font-semibold text-gray-800 group-hover:text-amber-700">{tier.name}</span>
                      </div>
                      <span className="text-xs font-bold text-amber-600 whitespace-nowrap">{tier.price}</span>
                    </Link>
                  ))}
                </div>
                <Link
                  to={guide.shopRec.ctaLink}
                  className="flex items-center justify-center gap-2 w-full bg-amber-500 hover:bg-amber-400 text-white text-xs font-bold py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-store-2-line text-sm"></i>
                  </div>
                  {guide.shopRec.ctaLabel}
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
